import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { auth as authApi } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("hla_user") || "null");
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem("hla_token"));
  const [loading, setLoading] = useState(false);

  const persist = useCallback((t, u) => {
    if (t) localStorage.setItem("hla_token", t);
    else localStorage.removeItem("hla_token");
    if (u) localStorage.setItem("hla_user", JSON.stringify(u));
    else localStorage.removeItem("hla_user");
    setToken(t || null);
    setUser(u || null);
  }, []);

  // Heuristic to extract token+user from any auth response shape
  const extract = (data) => {
    const t =
      data?.token ||
      data?.access_token ||
      data?.accessToken ||
      data?.jwt ||
      null;
    const u = data?.user || data?.profile || data?.account || data || null;
    return { t, u };
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const data = await authApi.login({ email, password });
      const { t, u } = extract(data);
      persist(t, u);
      return u;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password) => {
    setLoading(true);
    try {
      const data = await authApi.register({ email, password });
      const { t, u } = extract(data);
      persist(t, u);
      return u;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    persist(null, null);
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const u = await authApi.me();
      const me = u?.user || u;
      localStorage.setItem("hla_user", JSON.stringify(me));
      setUser(me);
    } catch {
      persist(null, null);
    }
  }, [token, persist]);

  useEffect(() => {
    if (token && !user) refreshUser();
  }, [token, user, refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
